import { useEffect, useState, useRef } from "react";
import { X, Play, Pause, SkipForward, ArrowRight, Upload, Stethoscope, Rocket, Handshake, RefreshCcw, LineChart } from "lucide-react";

/**
 * Iter 69.8/69.9: Video Tour animato (60s, 3 step da ~20s ciascuno).
 * Skippabile, pausabile, con progress bar.
 * Iter 69.9: due varianti — `role="admin"` e `role="manager"` con step diversi.
 */
const ADMIN_STEPS = [
  {
    id: "import",
    duration: 20000,
    icon: Upload,
    color: "cyan",
    title: "1. Carica il Listone e le Rose",
    subtitle: "Vai in Decisioni Admin → Import & Aggiornamenti",
    bullets: [
      "📥 Quotazioni: XLSX ufficiale Fantacalcio.it (Q.I./Q.A./FVM)",
      "🌐 Rose Fantacalcio: CSV con le 10 squadre della lega",
      "📅 Post-Calendario: XLSX con prezzi pagati e stipendi",
    ],
    footer: "Ordine consigliato: Quotazioni → Rose CSV → Post-Calendario",
  },
  {
    id: "salute",
    duration: 20000,
    icon: Stethoscope,
    color: "amber",
    title: "2. Sistema la Salute Rosa",
    subtitle: "Vai in Decisioni Admin → 🩺 Salute Rosa",
    bullets: [
      "🪄 Auto-Attach Fanta ID: collegamento automatico in un click",
      "♻️ Riconcilia Listone: refresha Q.I./Q.A. dal listone corrente",
      "🔧 Assegna manualmente: nomi troppo storpiati? Cerca a mano",
    ],
    footer: "0 giocatori orfani = rosa pronta per l'asta",
  },
  {
    id: "phase",
    duration: 20000,
    icon: Rocket,
    color: "purple",
    title: "3. Avanza la Fase con Milestone",
    subtitle: "Dal banner in alto: bottone AVANZA →",
    bullets: [
      "📸 Ogni fase suggerisce una milestone (T0-T5) pre-snapshot",
      "🕹️ 0→1→2 Import Rose · 3 Riparazione · 8 Pre-Asta · 9 Asta Furti",
      "💾 Snapshot automatico prima di avanzare → rollback sicuro",
    ],
    footer: "La lega evolve in modo tracciato e reversibile",
  },
];

const MANAGER_STEPS = [
  {
    id: "sponsor",
    duration: 20000,
    icon: Handshake,
    color: "emerald",
    title: "1. Candidati a uno Sponsor",
    subtitle: "Vai in menu Sponsor",
    bullets: [
      "💰 Ogni sponsor offre crediti upfront alla firma",
      "🎯 Ha un obiettivo (top 3, tot gol, clean sheet…) che sblocca il bonus",
      "🤝 Candidati liberamente, l'admin approva la trattativa",
    ],
    footer: "Sponsor giusti = più crediti per l'asta",
  },
  {
    id: "renewal",
    duration: 20000,
    icon: RefreshCcw,
    color: "amber",
    title: "2. Decidi Trattieni o Libera (Rinnovi)",
    subtitle: "Vai in menu Rinnovi (fase 8+)",
    bullets: [
      "🔒 Trattieni: rinnovi il giocatore pagando lo STIPENDIO",
      "🚪 Libera: il giocatore va all'asta furti, ricevi crediti_recuperati",
      "🎯 Asta Furti (fase 9): rilanci su liberati altrui",
    ],
    footer: "Salary = costo ricorrente. Scegli con criterio economico",
  },
  {
    id: "timeline",
    duration: 20000,
    icon: LineChart,
    color: "purple",
    title: "3. Studia la Timeline dei Giocatori",
    subtitle: "Clicca sul NOME di qualsiasi giocatore",
    bullets: [
      "📊 Timeline apre le stagioni passate (Q.I./Q.A./FVM)",
      "📈 Vedi delta interstagione (chi sale, chi scende)",
      "🎯 Decisione informata su trattieni/libera/asta",
    ],
    footer: "Dati storici = vantaggio competitivo",
  },
];

const COLOR_MAP = {
  cyan: { text: "text-cyan-300", bg: "bg-cyan-500/15", border: "border-cyan-500/40", grad: "from-cyan-500 to-cyan-700", ring: "ring-cyan-400/40" },
  amber: { text: "text-amber-300", bg: "bg-amber-500/15", border: "border-amber-500/40", grad: "from-amber-500 to-amber-700", ring: "ring-amber-400/40" },
  purple: { text: "text-purple-300", bg: "bg-purple-500/15", border: "border-purple-500/40", grad: "from-purple-500 to-purple-700", ring: "ring-purple-400/40" },
  emerald: { text: "text-emerald-300", bg: "bg-emerald-500/15", border: "border-emerald-500/40", grad: "from-emerald-500 to-emerald-700", ring: "ring-emerald-400/40" },
};

export default function VideoTour({ onClose, onSkip, role = "admin" }) {
  const STEPS = role === "manager" ? MANAGER_STEPS : ADMIN_STEPS;
  const [stepIdx, setStepIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const raf = useRef(null);
  const lastTs = useRef(null);
  const step = STEPS[stepIdx];
  const c = COLOR_MAP[step.color];

  useEffect(() => {
    const tick = (ts) => {
      if (paused) { lastTs.current = ts; raf.current = requestAnimationFrame(tick); return; }
      if (lastTs.current == null) lastTs.current = ts;
      const dt = ts - lastTs.current;
      lastTs.current = ts;
      setElapsed(e => {
        const next = e + dt;
        if (next >= step.duration) {
          if (stepIdx < STEPS.length - 1) {
            setStepIdx(stepIdx + 1);
            return 0;
          } else {
            // Finished
            cancelAnimationFrame(raf.current);
            setTimeout(() => onClose?.(), 400);
            return step.duration;
          }
        }
        return next;
      });
      raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
    // eslint-disable-next-line
  }, [stepIdx, paused]);

  const pct = Math.min(100, (elapsed / step.duration) * 100);
  const totalPct = ((stepIdx + elapsed / step.duration) / STEPS.length) * 100;
  const StepIcon = step.icon;

  return (
    <div className="fixed inset-0 z-[60] bg-black/90 backdrop-blur-md flex items-center justify-center p-4" data-testid="video-tour">
      <div className="w-full max-w-2xl rounded-3xl bg-gradient-to-br from-slate-900 via-slate-950 to-black border border-slate-800 shadow-2xl overflow-hidden">
        {/* Header progress */}
        <div className="p-4 border-b border-slate-800/60 flex items-center gap-3">
          <div className="text-[10px] tracking-[0.3em] text-slate-400 uppercase font-bold shrink-0">Tour · 60s</div>
          <div className="flex-1 h-1.5 bg-slate-800 rounded-full overflow-hidden">
            <div className={`h-full bg-gradient-to-r ${c.grad} transition-all`} style={{ width: `${totalPct}%` }}/>
          </div>
          <div className="text-[10px] font-mono text-slate-500 shrink-0">{stepIdx + 1}/{STEPS.length}</div>
          <button onClick={() => setPaused(!paused)} data-testid="tour-pause"
            className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300">
            {paused ? <Play size={14}/> : <Pause size={14}/>}
          </button>
          <button onClick={onSkip} data-testid="tour-skip"
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-400 text-[10px] font-bold uppercase tracking-widest">
            <SkipForward size={11}/> Skip
          </button>
          <button onClick={onClose} data-testid="tour-close" className="text-slate-500 hover:text-white">
            <X size={18}/>
          </button>
        </div>

        {/* Step content */}
        <div key={step.id} className="p-8 space-y-5 min-h-[380px] animate-fadeIn">
          <div className="flex items-center gap-4">
            <div className={`w-20 h-20 rounded-3xl ${c.bg} border ${c.border} flex items-center justify-center relative overflow-hidden`}>
              <StepIcon className={c.text} size={40}/>
              <div className={`absolute inset-0 rounded-3xl ring-4 ${c.ring} animate-pulse`}/>
            </div>
            <div>
              <div className={`text-[10px] tracking-[0.3em] uppercase font-black ${c.text}`}>Step {stepIdx + 1}</div>
              <h2 className="text-3xl font-black text-white leading-tight">{step.title}</h2>
              <div className="text-sm text-slate-400 mt-1">{step.subtitle}</div>
            </div>
          </div>

          <div className="space-y-2 pl-1">
            {step.bullets.map((b, i) => (
              <div key={i} data-testid={`tour-bullet-${stepIdx}-${i}`}
                className="flex items-start gap-3 opacity-0 animate-slideIn"
                style={{ animationDelay: `${i * 250}ms`, animationFillMode: "forwards" }}>
                <ArrowRight className={`${c.text} shrink-0 mt-0.5`} size={16}/>
                <span className="text-slate-200 text-sm">{b}</span>
              </div>
            ))}
          </div>

          <div className={`rounded-xl border ${c.border} ${c.bg} p-3 text-xs italic ${c.text} font-medium`}>
            💡 {step.footer}
          </div>

          {/* Step-specific mini animation */}
          <div className="rounded-2xl bg-slate-950/60 border border-slate-800 p-4">
            <MiniAnimation step={step.id} color={c}/>
          </div>
        </div>

        {/* Step progress within current step */}
        <div className="h-0.5 bg-slate-900">
          <div className={`h-full bg-gradient-to-r ${c.grad} transition-all`} style={{ width: `${pct}%`, transition: paused ? "none" : undefined }}/>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes slideIn { from { opacity: 0; transform: translateX(-12px); } to { opacity: 1; transform: translateX(0); } }
        .animate-fadeIn { animation: fadeIn 350ms ease-out; }
        .animate-slideIn { animation: slideIn 400ms ease-out; }
      `}</style>
    </div>
  );
}

function MiniAnimation({ step, color }) {
  if (step === "import") {
    return (
      <div className="flex items-center justify-around gap-2 text-[10px] font-bold">
        {["QUOTAZIONI", "ROSE CSV", "POST-CALENDARIO"].map((l, i) => (
          <div key={l} className="flex items-center gap-1">
            <div className={`w-10 h-12 rounded-md ${color.bg} border ${color.border} flex items-center justify-center animate-bounce`} style={{ animationDelay: `${i * 300}ms` }}>
              <div className={`w-5 h-6 ${color.text} font-mono`}>XLS</div>
            </div>
            {i < 2 && <ArrowRight size={12} className="text-slate-600"/>}
          </div>
        ))}
      </div>
    );
  }
  if (step === "salute") {
    return (
      <div className="grid grid-cols-4 gap-1.5">
        {Array.from({ length: 24 }).map((_, i) => {
          const status = i < 20 ? "green" : "amber";
          return (
            <div key={i} className={`h-5 rounded ${status === "green" ? "bg-emerald-500/60" : "bg-amber-500/60"} ${status === "amber" ? "animate-pulse" : ""}`}
              style={{ animationDelay: `${i * 40}ms` }}/>
          );
        })}
      </div>
    );
  }
  if (step === "phase") {
    return (
      <div className="flex items-center justify-between text-[10px] font-mono font-black">
        {["0", "1", "2", "3", "…", "8", "9"].map((n, i) => (
          <div key={n} className="flex items-center gap-1">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center border ${i <= 2 ? `${color.bg} ${color.border} ${color.text}` : "border-slate-700 text-slate-600"}`}>
              {n}
            </div>
            {i < 6 && <ArrowRight size={10} className="text-slate-700"/>}
          </div>
        ))}
      </div>
    );
  }
  if (step === "sponsor") {
    return (
      <div className="flex items-center justify-around gap-2">
        {["+50cr", "OBJ 🎯", "+150cr"].map((l, i) => (
          <div key={l} className="flex items-center gap-1">
            <div className={`px-3 py-2 rounded-lg ${color.bg} border ${color.border} ${color.text} font-black text-sm animate-pulse`} style={{ animationDelay: `${i * 400}ms` }}>{l}</div>
            {i < 2 && <ArrowRight size={12} className="text-slate-600"/>}
          </div>
        ))}
      </div>
    );
  }
  if (step === "renewal") {
    return (
      <div className="flex items-center justify-around gap-3 text-[10px] font-bold">
        <div className={`px-4 py-2 rounded-lg bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 animate-pulse`}>🔒 TRATTIENI<br/><span className="text-[9px] opacity-70">+salary</span></div>
        <div className="text-slate-500 font-black">OR</div>
        <div className={`px-4 py-2 rounded-lg bg-rose-500/15 border border-rose-500/40 text-rose-300 animate-pulse`} style={{ animationDelay: "500ms" }}>🚪 LIBERA<br/><span className="text-[9px] opacity-70">+recuperati</span></div>
      </div>
    );
  }
  if (step === "timeline") {
    // small line chart: 3 dots ascending
    return (
      <div className="flex items-end justify-around gap-2 h-16">
        {[24, 32, 28, 45, 52].map((h, i) => (
          <div key={i} className="flex flex-col items-center gap-1">
            <div className={`w-6 rounded-t ${color.bg} border-t border-l border-r ${color.border} transition-all`}
              style={{ height: `${h}px`, animation: `growUp 800ms ease-out ${i * 100}ms both` }}/>
            <div className={`text-[9px] font-mono ${color.text}`}>{['23/24','24/25','25/26','26/27','TREND'][i]}</div>
          </div>
        ))}
        <style>{`@keyframes growUp { from { height: 0; } to { height: attr(data-h px); } }`}</style>
      </div>
    );
  }
  return null;
}
